import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, ListGroup, Image, Alert, Spinner, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { getCastaways } from '../api/castaways';
import { Castaway } from '../types';
import TribeBadge, { getTribeColor } from './TribeBadge';
import AdminMockDraft from './AdminMockDraft';
import CastawayBioModal from './CastawayBioModal';
import '../App.css';

interface League {
    id: string;
    name: string;
    season?: number;
    active?: boolean;
    readyToDraft?: boolean;
    draftDeadline?: string | null;
  rosterSize?: number;
  activeRosterSize?: number;
    lineupDeadlines?: { [episode: string]: string };
    tribes?: { name: string; color: string }[];
}

const DraftPage: React.FC = () => {
  const [allCastaways, setAllCastaways] = useState<Castaway[]>([]);
  const [availableCastaways, setAvailableCastaways] = useState<Castaway[]>([]);
  const [tribes, setTribes] = useState<{ name: string; color: string }[]>([]);
  const [myTeam, setMyTeam] = useState<Castaway[]>([]);
  const [savedTeam, setSavedTeam] = useState<Castaway[]>([]);
  const [hasSavedDraft, setHasSavedDraft] = useState<boolean>(false);
  const [rosterSize, setRosterSize] = useState(3);
  const [activeRosterSize, setActiveRosterSize] = useState(3);
  const [activeTeam, setActiveTeam] = useState<Castaway[]>([]);
  const [eliminatedCastawayIds, setEliminatedCastawayIds] = useState<Set<number>>(new Set());
  const [currentEpisode, setCurrentEpisode] = useState<number | null>(null);
  const [episodeDeadline, setEpisodeDeadline] = useState<string | null>(null);
  const [rosterLocked, setRosterLocked] = useState(false);
  const [userInfo, setUserInfo] = useState<{ userName: string, leagueId: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [leagueDeadline, setLeagueDeadline] = useState<string | null>(null);
  const [currentLeague, setCurrentLeague] = useState<League | null>(null);
  const [isDraftLive, setIsDraftLive] = useState<boolean>(false);
  const [showPracticeMode, setShowPracticeMode] = useState<boolean>(false);
  const [checkingLeague, setCheckingLeague] = useState<boolean>(true);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => {
    const saved = localStorage.getItem('selectedSeason');
    return saved ? parseInt(saved, 10) : 51;
  });
  const [selectedBioCastaway, setSelectedBioCastaway] = useState<Castaway | null>(null);
  const navigate = useNavigate();

  const isDeadlinePassed = Boolean(leagueDeadline && new Date() > new Date(leagueDeadline));

  const loadUserInfo = useCallback(() => {
    const userKey = `fantasyUser_${selectedSeason}`;
    const storedUser = localStorage.getItem(userKey);
    if (storedUser) {
      setUserInfo(JSON.parse(storedUser));
      setError(null); // Clear errors if user is found
    } else {
      setUserInfo(null);
    }
  }, [selectedSeason]);

  useEffect(() => {
    loadUserInfo();

    const handleSeasonChange = () => {
      const newSeason = parseInt(localStorage.getItem('selectedSeason') || '51', 10);
      setSelectedSeason(newSeason);
      setMyTeam([]);
      setSavedTeam([]);
      setHasSavedDraft(false);
      setAvailableCastaways([]);
      setRosterLocked(false);
      setLeagueDeadline(null);
      setSuccess(null);
      setShowPracticeMode(false);
    };
    window.addEventListener('seasonChanged', handleSeasonChange);
    return () => window.removeEventListener('seasonChanged', handleSeasonChange);
  }, [loadUserInfo]);

  // Check if draft is officially live for this season
  useEffect(() => {
    let isMounted = true;
    const checkLeagueStatus = async () => {
      setCheckingLeague(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`);
        if (response.ok && isMounted) {
          const data = await response.json();
          const seasonLeagues = (data.leagues || []).filter((l: any) => (l.season || 49) === selectedSeason);
          const leagueForUser = userInfo ? seasonLeagues.find((l: any) => l.id === userInfo.leagueId) : seasonLeagues[0];
          if (leagueForUser) {
            setCurrentLeague(leagueForUser);
            const live = Boolean(leagueForUser.readyToDraft ?? leagueForUser.active);
            setIsDraftLive(live);
            setRosterSize(leagueForUser.rosterSize || (selectedSeason >= 51 ? 5 : 3));
            setActiveRosterSize(leagueForUser.activeRosterSize || 3);
            if (leagueForUser.tribes) setTribes(leagueForUser.tribes);
            if (leagueForUser.draftDeadline) {
              setLeagueDeadline(leagueForUser.draftDeadline);
            }
          } else {
            setCurrentLeague(null);
            setIsDraftLive(false);
          }
        }
      } catch (e) {
        console.error("Failed to check league status", e);
      } finally {
        if (isMounted) setCheckingLeague(false);
      }
    };
    checkLeagueStatus();
    return () => { isMounted = false; };
  }, [userInfo, selectedSeason]);

  useEffect(() => {
    if (userInfo) {
      const loadCastaways = async () => {
        setIsLoading(true);
        try {
          const castaways = await getCastaways(userInfo.leagueId);
          setAllCastaways(castaways);
          setAvailableCastaways(castaways);
        } catch (err) {
          setError('Could not load castaway data.');
        } finally {
          setIsLoading(false);
        }
      };
      loadCastaways();
    }
  }, [userInfo]);
  
  const getCastawayById = useCallback((id: number) => allCastaways.find(c => c.id === id), [allCastaways]);

  useEffect(() => {
    if (!userInfo) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [draftsResponse, leaguesResponse, scoresResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${userInfo.leagueId}/drafts`),
          fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`),
          fetch(`${process.env.REACT_APP_API_URL}/api/scores/${selectedSeason}`),
        ]);
        
        if (!draftsResponse.ok) throw new Error('Failed to fetch existing drafts');
        if (!leaguesResponse.ok) throw new Error('Failed to fetch league details');

        const draftsData = await draftsResponse.json();
        const leaguesData = await leaguesResponse.json();
        const scoresData = scoresResponse.ok ? await scoresResponse.json() : { scores: {} };
        const eliminatedIds = new Set<number>();
        Object.values(scoresData.scores || {}).forEach((episodeScores: any) => {
          Object.entries(episodeScores || {}).forEach(([id, score]: [string, any]) => {
            if (score?.votedOut || score?.involuntaryEvacuation || score?.voluntaryExit) eliminatedIds.add(Number(id));
          });
        });
        setEliminatedCastawayIds(eliminatedIds);

        const currentLeague = leaguesData.leagues.find((l: any) => l.id === userInfo.leagueId);
        
        // Season mismatch check
        if (currentLeague) {
            const leagueSeason = currentLeague.season || 49;
            if (leagueSeason !== selectedSeason) {
                setUserInfo(null);
                setIsLoading(false);
                return;
            }
            setRosterSize(currentLeague.rosterSize || (selectedSeason >= 51 ? 5 : 3));
            setActiveRosterSize(currentLeague.activeRosterSize || 3);
            if (currentLeague.tribes) setTribes(currentLeague.tribes);
            if (currentLeague.draftDeadline) {
              setLeagueDeadline(currentLeague.draftDeadline);
            }
            const nextEpisode = (Object.entries(currentLeague.lineupDeadlines || {}) as [string, string][])
              .filter(([, deadline]) => new Date(deadline).getTime() > Date.now())
              .sort(([a], [b]) => Number(a) - Number(b))[0];
            setCurrentEpisode(nextEpisode ? Number(nextEpisode[0]) : null);
            setEpisodeDeadline(nextEpisode ? nextEpisode[1] : null);
        }

        const userDraft = draftsData.drafts?.[userInfo.userName];
        if (userDraft && Array.isArray(userDraft.team) && userDraft.team.length > 0) {
          const loadedTeam = userDraft.team.map((id: number) => getCastawayById(id)).filter(Boolean) as Castaway[];
          setMyTeam(loadedTeam);
          setSavedTeam(loadedTeam);
          setHasSavedDraft(true);
          setRosterLocked(true);

          if ((currentLeague?.rosterSize || 3) > 3) {
            const nextEpisode = (Object.entries(currentLeague?.lineupDeadlines || {}) as [string, string][])
              .filter(([, deadline]) => new Date(deadline).getTime() > Date.now())
              .sort(([a], [b]) => Number(a) - Number(b))[0];
            const episode = nextEpisode ? nextEpisode[0] : '1';
            const lineupIds = userDraft.lineupHistory?.[episode] || userDraft.team.slice(0, 3);
            setActiveTeam(lineupIds.map((id: number) => getCastawayById(id)).filter(Boolean) as Castaway[]);
          }
          const draftedIds = new Set(userDraft.team);
          setAvailableCastaways(allCastaways.filter(c => !draftedIds.has(c.id)));
        } else {
          setHasSavedDraft(false);
          setSavedTeam([]);
          setRosterLocked(false);
          setAvailableCastaways(allCastaways);
        }
      } catch (err: any) {
        setError('Could not load draft data: ' + err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [userInfo, selectedSeason, allCastaways, getCastawayById]);

  const draftCastaway = (castaway: Castaway) => {
    if (isDeadlinePassed) {
      alert(`The draft deadline has passed (${new Date(leagueDeadline!).toLocaleString()}).`);
      return;
    }
    if (rosterLocked) {
      alert('Your roster is currently locked. Click "Unlock & Edit Roster" to make changes.');
      return;
    }
    if (myTeam.length < rosterSize) {
      setMyTeam(prev => [...prev, castaway]);
      setAvailableCastaways(prev => prev.filter(c => c.id !== castaway.id));
    } else {
      alert(`Your roster is full! You can only pick ${rosterSize} castaways.`);
    }
  };

  const removeCastaway = (castaway: Castaway) => {
    if (isDeadlinePassed) {
      alert(`The draft deadline has passed (${new Date(leagueDeadline!).toLocaleString()}).`);
      return;
    }
    if (rosterLocked) {
      alert('Your roster is currently locked. Click "Unlock & Edit Roster" to make changes.');
      return;
    }
    setMyTeam(prev => prev.filter(c => c.id !== castaway.id));
    setAvailableCastaways(prev => [...prev, castaway].sort((a, b) => a.id - b.id));
  };

  const handleRandomDraft = () => {
    if (isDeadlinePassed) {
      alert(`The draft deadline has passed (${new Date(leagueDeadline!).toLocaleString()}).`);
      return;
    }
    if (rosterLocked) {
      alert('Your roster is currently locked. Click "Unlock & Edit Roster" to make changes.');
      return;
    }
    if (allCastaways.length === 0) return;

    // Filter out castaways who were already eliminated prior to draft/premiere if any
    const eligiblePool = allCastaways.filter(c => !eliminatedCastawayIds.has(c.id));
    const pool = eligiblePool.length >= rosterSize ? eligiblePool : allCastaways;
    const shuffled = [...pool].sort(() => 0.5 - Math.random());
    const randomPicks = shuffled.slice(0, rosterSize);

    setMyTeam(randomPicks);
    const pickedIds = new Set(randomPicks.map(c => c.id));
    setAvailableCastaways(allCastaways.filter(c => !pickedIds.has(c.id)));
    setSuccess(`🎲 Randomly selected ${rosterSize} castaways for your roster! Click "Lock In Roster" when ready.`);
    setError(null);
  };

  const handleClearRoster = () => {
    if (isDeadlinePassed || rosterLocked) return;
    setMyTeam([]);
    setAvailableCastaways([...allCastaways]);
    setError(null);
    setSuccess(null);
  };

  const handleUnlockRoster = () => {
    if (isDeadlinePassed) {
      alert(`The draft deadline has passed (${new Date(leagueDeadline!).toLocaleString()}). Rosters are permanently locked.`);
      return;
    }
    setRosterLocked(false);
    setSuccess('🔓 Draft unlocked! You can now add or remove players from your roster. Remember to click "Lock In Updated Roster" when you are finished.');
    setError(null);
  };

  const handleCancelEdit = () => {
    setMyTeam(savedTeam);
    const savedIds = new Set(savedTeam.map(c => c.id));
    setAvailableCastaways(allCastaways.filter(c => !savedIds.has(c.id)));
    setRosterLocked(true);
    setError(null);
    setSuccess(null);
  };

  const handleLockInTeam = async () => {
    if (leagueDeadline && new Date() > new Date(leagueDeadline)) {
      setError(`The draft deadline has passed (${new Date(leagueDeadline).toLocaleString()}). You cannot lock in your team.`);
      return;
    }
    if (myTeam.length !== rosterSize) {
      setError(`You must select exactly ${rosterSize} castaways.`);
      return;
    }
    if (!userInfo) {
      setError('User information not found. Please join a league again.');
      navigate('/join');
      return;
    }

    const confirmMsg = hasSavedDraft
      ? `Save and lock your updated ${rosterSize}-player roster? (You can still unlock and edit anytime before the draft deadline).`
      : `Lock in your ${rosterSize}-player draft? (You can still unlock and edit anytime before the draft deadline).`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const teamIds = myTeam.map(c => c.id);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${userInfo.leagueId}/draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userName: userInfo.userName, team: teamIds }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save draft');
      }

      setSuccess(`✅ Roster successfully locked for ${userInfo.userName}! You can unlock and edit your picks anytime until the draft deadline.`);
      setSavedTeam(myTeam);
      setHasSavedDraft(true);
      setRosterLocked(true);
    } catch (err: any) {
      setError(err.message || 'An error occurred while saving draft.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleActiveCastaway = (castaway: Castaway) => {
    if (eliminatedCastawayIds.has(castaway.id)) return;
    if (activeTeam.some(player => player.id === castaway.id)) {
      setActiveTeam(activeTeam.filter(player => player.id !== castaway.id));
    } else if (activeTeam.length < activeRosterSize) {
      setActiveTeam([...activeTeam, castaway]);
    }
  };

  const saveActiveLineup = async () => {
    if (!userInfo || !currentEpisode || !episodeDeadline) return;
    if (activeTeam.length > activeRosterSize) {
      setError(`Choose up to ${activeRosterSize} active players.`);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${userInfo.leagueId}/lineup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: userInfo.userName, episode: currentEpisode, activeTeam: activeTeam.map(player => player.id) }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save active lineup');
      setSuccess(data.message);
    } catch (err: any) {
      setError(err.message || 'Failed to save active lineup');
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingLeague) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  // Before the draft is live, show Mock Draft practice mode
  if (!isDraftLive) {
    return (
      <Container className="mt-4 mb-5">
        <Alert variant="warning" className="text-center p-3 mb-4 shadow-sm border">
          <h4 className="alert-heading mb-2">
            <i className="bi bi-clock-history"></i> Season {selectedSeason} Draft is Not Live Yet
          </h4>
          <p className="mb-2">
            The official draft will open right after Episode 1 wraps on September 23!
          </p>
          <p className="mb-0 text-muted small">
            In the meantime, you can test and practice the new 5-player roster and weekly active/bench lineups below in <strong>Mock Draft Practice Mode</strong>.
          </p>
        </Alert>
        <AdminMockDraft userMode={true} defaultSeason={selectedSeason} />
      </Container>
    );
  }

  // If draft is live, but user clicks to practice in mock mode
  if (showPracticeMode) {
    return (
      <Container className="mt-4 mb-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Button variant="primary" onClick={() => setShowPracticeMode(false)}>
            &larr; Return to Official Draft
          </Button>
          <Badge bg="secondary" className="px-3 py-2">Practice Mode</Badge>
        </div>
        <AdminMockDraft userMode={true} defaultSeason={selectedSeason} />
      </Container>
    );
  }

  // If draft is live but user hasn't joined a league yet
  if (!userInfo) {
    return (
      <Container className="mt-4 mb-5">
        <Alert variant="info" className="text-center p-4 shadow-sm border mb-4">
          <h4 className="alert-heading mb-3">Draft is Live for Season {selectedSeason}!</h4>
          <p className="mb-3">
            You haven't joined a league entry yet for Season {selectedSeason}. Join a league to submit your official roster.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Link to={`/join?season=${selectedSeason}`} className="btn btn-primary">
              Join a League
            </Link>
            <Button variant="outline-success" onClick={() => setShowPracticeMode(true)}>
              Practice with Mock Draft
            </Button>
          </div>
        </Alert>
        <AdminMockDraft userMode={true} defaultSeason={selectedSeason} />
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-0">Season {selectedSeason}: Draft Your Team, {userInfo.userName}! (Select {rosterSize})</h2>
          <h5 className="text-muted mb-0">{currentLeague?.name || `League: ${userInfo.leagueId}`}</h5>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {!rosterLocked && !isDeadlinePassed && (
            <>
              <Button variant="outline-primary" size="sm" onClick={handleRandomDraft} disabled={allCastaways.length === 0}>
                🎲 Quick Random Draft ({rosterSize})
              </Button>
              {myTeam.length > 0 && (
                <Button variant="outline-danger" size="sm" onClick={handleClearRoster}>
                  Clear Picks
                </Button>
              )}
            </>
          )}
          <Button variant="outline-success" size="sm" onClick={() => setShowPracticeMode(true)}>
            Practice with Mock Draft
          </Button>
        </div>
      </div>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
      
      {/* Draft Status & Deadline Banner */}
      {isDeadlinePassed ? (
        <Alert variant="danger" className="d-flex justify-content-between align-items-center flex-wrap gap-2 shadow-sm">
          <span>🔒 <strong>Draft Closed:</strong> The deadline has passed ({new Date(leagueDeadline!).toLocaleString()}). Rosters are permanently locked for the season.</span>
          <Badge bg="danger">Deadline Passed</Badge>
        </Alert>
      ) : rosterLocked && hasSavedDraft ? (
        <Alert variant="success" className="d-flex justify-content-between align-items-center flex-wrap gap-2 shadow-sm">
          <div>
            <strong>✅ Your roster is locked in.</strong> {leagueDeadline ? `You can unlock and edit your picks anytime until the deadline on ${new Date(leagueDeadline).toLocaleString()}.` : 'You can unlock and edit your picks before the deadline.'}
          </div>
          <Button variant="primary" size="sm" onClick={handleUnlockRoster} className="fw-bold">
            🔓 Unlock & Edit Roster
          </Button>
        </Alert>
      ) : hasSavedDraft && !rosterLocked ? (
        <Alert variant="warning" className="d-flex justify-content-between align-items-center flex-wrap gap-2 shadow-sm border-warning">
          <div>
            <strong>✏️ Editing Mode:</strong> Make changes to your picks below and click <strong>"Lock In Updated Roster"</strong> before {leagueDeadline ? new Date(leagueDeadline).toLocaleString() : 'the deadline'}.
          </div>
          <Button variant="outline-secondary" size="sm" onClick={handleCancelEdit}>
            Cancel Changes
          </Button>
        </Alert>
      ) : (
        <Alert variant="info" className="d-flex justify-content-between align-items-center flex-wrap gap-2 shadow-sm">
          <span>Select your <strong>{rosterSize} castaways</strong> below and click <strong>"Lock In Roster"</strong> {leagueDeadline ? `before ${new Date(leagueDeadline).toLocaleString()}` : ''}.</span>
          {leagueDeadline && <Badge bg="primary">Due {new Date(leagueDeadline).toLocaleDateString()}</Badge>}
        </Alert>
      )}

      {rosterSize > 3 && !currentEpisode && rosterLocked && (
        <Alert variant="warning">The next episode lineup is not available yet. An admin must configure its 7pm ET deadline.</Alert>
      )}

      <Row>
        {/* Available Castaways */}
        <Col md={7}>
          <Card className="mb-4 home-card shadow-sm">
            <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
              <span>Available Castaways</span>
              <small className="text-muted fw-normal" style={{ fontSize: '0.8rem' }}>Click name or photo for bio</small>
            </Card.Header>
            <ListGroup variant="flush">
              {availableCastaways.map(castaway => (
                <ListGroup.Item key={castaway.id} className="d-flex justify-content-between align-items-center">
                  <div 
                    className="d-flex align-items-center cursor-pointer" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedBioCastaway(castaway)}
                    title="Click to view full contestant bio & quotes"
                  >
                    <Image
                      src={castaway.imageUrl}
                      roundedCircle
                      referrerPolicy="no-referrer"
                      style={{
                        width: '50px',
                        height: '50px',
                        marginRight: '15px',
                        objectFit: 'cover',
                        border: `3px solid ${getTribeColor(castaway.tribe, tribes, castaway.tribeColor)}`
                      }}
                    />
                    <div>
                      <strong className="text-primary text-decoration-underline-hover">{castaway.name}</strong> ({castaway.age}) <span className="text-muted small">ℹ️</span><br />
                      <div className="d-flex align-items-center gap-1 mt-1">
                        <TribeBadge tribeName={castaway.tribe} tribes={tribes} tribeColor={castaway.tribeColor} />
                        <small className="text-muted">{castaway.occupation}</small>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => setSelectedBioCastaway(castaway)}
                      title="View Bio"
                    >
                      Bio
                    </Button>
                    <Button 
                      variant="success" 
                      size="sm" 
                      onClick={() => draftCastaway(castaway)}
                      disabled={myTeam.length >= rosterSize || isLoading || isDeadlinePassed || rosterLocked}
                      title={rosterLocked ? 'Unlock roster to add players' : 'Draft castaway'}
                    >
                      Draft
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card>
        </Col>

        {/* My Team */}
        <Col md={5}>
          <Card className="home-card shadow-sm">
            <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
              <span>My Roster ({myTeam.length}/{rosterSize})</span>
              {rosterLocked && !isDeadlinePassed && (
                <Badge bg="success" style={{ fontSize: '0.72rem' }}>Locked</Badge>
              )}
            </Card.Header>
            <ListGroup variant="flush">
              {myTeam.map(castaway => (
                <ListGroup.Item key={castaway.id} className="d-flex justify-content-between align-items-center">
                  <div 
                    className="d-flex align-items-center cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedBioCastaway(castaway)}
                    title="Click to view full contestant bio"
                  >
                    <Image
                      src={castaway.imageUrl}
                      roundedCircle
                      referrerPolicy="no-referrer"
                      style={{
                        width: '40px',
                        height: '40px',
                        marginRight: '10px',
                        objectFit: 'cover',
                        border: `2px solid ${getTribeColor(castaway.tribe, tribes, castaway.tribeColor)}`
                      }}
                    />
                    <div>
                      <strong className="text-primary">{castaway.name}</strong>
                      <div className="mt-1">
                        <TribeBadge tribeName={castaway.tribe} tribes={tribes} tribeColor={castaway.tribeColor} style={{ fontSize: '0.65rem' }} />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => setSelectedBioCastaway(castaway)}
                      title="View Bio"
                    >
                      Bio
                    </Button>
                    <Button 
                      variant="danger" 
                      size="sm" 
                      onClick={() => removeCastaway(castaway)} 
                      disabled={isLoading || isDeadlinePassed || rosterLocked}
                      title={rosterLocked ? 'Unlock roster to remove players' : 'Remove castaway'}
                    >
                      Remove
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
              {myTeam.length === 0 && <ListGroup.Item>No castaways drafted yet.</ListGroup.Item>}
            </ListGroup>
          </Card>

          {/* Roster Action Buttons */}
          <div className="mt-3 text-center">
            {isDeadlinePassed ? (
              <Button variant="secondary" size="lg" disabled className="w-100">
                🔒 Draft Deadline Passed (Locked)
              </Button>
            ) : rosterLocked && hasSavedDraft ? (
              <Button 
                variant="primary" 
                size="lg" 
                onClick={handleUnlockRoster}
                className="w-100 fw-bold"
              >
                🔓 Unlock & Edit Roster
              </Button>
            ) : (
              <div className="d-flex gap-2 justify-content-center">
                <Button 
                  variant="success" 
                  size="lg" 
                  onClick={handleLockInTeam}
                  disabled={myTeam.length !== rosterSize || isLoading}
                  className="w-100 fw-bold"
                >
                  {isLoading ? <Spinner animation="border" size="sm" /> : (hasSavedDraft ? '💾 Lock In Updated Roster' : '🔒 Lock In Roster')}
                </Button>
                {hasSavedDraft && (
                  <Button
                    variant="outline-secondary"
                    size="lg"
                    onClick={handleCancelEdit}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            )}
          </div>

          {rosterSize > 3 && rosterLocked && (
            <Card className="home-card shadow-sm mt-3 border-primary">
              <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
                <span>⚡ Active Lineup for Episode {currentEpisode || 'Next'}</span>
                <Badge bg="success" className="px-2 py-1">
                  {activeTeam.length}/{activeRosterSize} Active
                </Badge>
              </Card.Header>
              <Card.Body>
                {episodeDeadline ? (
                  <>
                    <p className="small text-muted mb-3">
                      Select up to {activeRosterSize} active scoring players before the deadline. Tap a player to activate or bench.
                    </p>
                    <ListGroup variant="flush">
                      {myTeam.map(castaway => {
                        const isActive = activeTeam.some(player => player.id === castaway.id);
                        const isEliminated = eliminatedCastawayIds.has(castaway.id);
                        const tribeColor = getTribeColor(castaway.tribe, tribes, castaway.tribeColor);

                        return (
                          <ListGroup.Item
                            key={castaway.id}
                            className={`d-flex justify-content-between align-items-center p-2 mb-2 rounded border ${isActive ? 'border-success' : ''}`}
                            style={{
                              backgroundColor: isActive ? 'rgba(240, 253, 244, 0.95)' : 'rgba(255, 255, 255, 0.9)',
                              cursor: isEliminated ? 'not-allowed' : 'pointer',
                              opacity: isEliminated ? 0.6 : 1
                            }}
                            onClick={() => !isEliminated && toggleActiveCastaway(castaway)}
                          >
                            <div className="d-flex align-items-center">
                              <Image
                                src={castaway.imageUrl}
                                roundedCircle
                                referrerPolicy="no-referrer"
                                style={{
                                  width: '38px',
                                  height: '38px',
                                  marginRight: '10px',
                                  objectFit: 'cover',
                                  border: `2px solid ${tribeColor}`
                                }}
                              />
                              <div>
                                <span className="fw-bold text-dark">{castaway.name}</span>
                                <div className="d-flex align-items-center gap-1">
                                  <TribeBadge tribeName={castaway.tribe} tribes={tribes} tribeColor={castaway.tribeColor} style={{ fontSize: '0.65rem' }} />
                                </div>
                              </div>
                            </div>
                            <div>
                              {isEliminated ? (
                                <Badge bg="danger">Eliminated</Badge>
                              ) : isActive ? (
                                <Badge bg="success" className="px-2 py-1">🟢 Active 3</Badge>
                              ) : (
                                <Badge bg="secondary" className="px-2 py-1">⏸️ Bench</Badge>
                              )}
                            </div>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                    <Button className="w-100 mt-3" variant="success" onClick={saveActiveLineup} disabled={isLoading || activeTeam.length === 0}>
                      {isLoading ? 'Saving...' : `💾 Save Episode ${currentEpisode || 'Lineup'} Active Lineup`}
                    </Button>
                  </>
                ) : (
                  <Alert variant="warning" className="mb-0">The episode deadline has not been configured yet.</Alert>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
      <div className="text-center mt-4">
        <Link to="/" className="btn btn-secondary">Back to Home</Link>
      </div>

      {/* Castaway Bio Popout Modal */}
      <CastawayBioModal
        show={Boolean(selectedBioCastaway)}
        onHide={() => setSelectedBioCastaway(null)}
        castaway={selectedBioCastaway}
        tribes={tribes}
        onDraft={(castaway) => draftCastaway(castaway)}
        isDraftable={!isDeadlinePassed && !rosterLocked && myTeam.length < rosterSize}
        isDrafted={Boolean(selectedBioCastaway && myTeam.some(c => c.id === selectedBioCastaway.id))}
      />
    </Container>
  );
};

export default DraftPage;